// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { NameRec, SeriesKey } from '../types';
import { searchNames } from '../data';
import { seriesLabel } from '../colours';
import { lineChart, type LineSeries } from '../charts';
import { escapeHtml, formatNumber } from '../utils/format';
import { segHtml, viewHead } from './shared';

const PALETTE = ['#d1613f', '#3d7f9d', '#c9527e', '#3f9d6b', '#8a6bbf', '#bd7a1e', '#2e7d9a', '#b3603f'];

function primarySeries(n: NameRec): SeriesKey {
  let best: SeriesKey = (Object.keys(n.series)[0] as SeriesKey) || 'NSW-boy';
  let bestTotal = -1;
  for (const k of Object.keys(n.series) as SeriesKey[]) {
    const t = (n.series[k] || []).reduce((s, p) => s + p[2], 0);
    if (t > bestTotal) {
      bestTotal = t;
      best = k;
    }
  }
  return best;
}

export function renderFinder(root: HTMLElement, ctx: ViewContext): void {
  const { names, meta } = ctx.data;
  let metric: 'rank' | 'count' = 'rank';

  // Seed with this year's #1 boy and girl (NSW) plus a classic faller for contrast.
  const seed = ['NOAH', 'CHARLOTTE', 'JOHN'].filter((k) => ctx.data.byKey.has(k));
  const selected: NameRec[] = seed.map((k) => ctx.data.byKey.get(k) as NameRec);

  root.innerHTML = `
    ${viewHead(
      'Name Finder',
      'Type any name to chart how popular it has been, year by year. Add several to compare them side by side — each name is shown for the state and sex where it appears most.',
    )}
    <div class="panel">
      <div class="controls">
        <div class="search-wrap" style="width:min(280px,80vw)">
          <input class="text-input" data-role="add" type="search" placeholder="Add a name…" aria-label="Add a name" autocomplete="off" style="width:100%" />
          <div class="search-results" data-role="sugg" role="listbox"></div>
        </div>
        <span class="field-label">Show:</span>
        ${segHtml('Metric', [
          { id: 'rank', label: 'Rank' },
          { id: 'count', label: 'Babies per year' },
        ], metric)}
      </div>
      <div class="selected-names" data-role="tags"></div>
      <div class="chart-frame" data-role="chart"></div>
      <p class="zoom-hint">Rank 1 is the most popular. A gap in a line means the name fell out of the top 100 that year. Click a name tag or a point to open its full profile.</p>
    </div>
  `;

  const tagsEl = root.querySelector('[data-role="tags"]') as HTMLElement;
  const chartEl = root.querySelector('[data-role="chart"]') as HTMLElement;
  const addInput = root.querySelector('[data-role="add"]') as HTMLInputElement;
  const sugg = root.querySelector('[data-role="sugg"]') as HTMLElement;

  function colourFor(i: number): string {
    return PALETTE[i % PALETTE.length];
  }

  function paint(): void {
    // tags
    tagsEl.innerHTML = selected
      .map(
        (n, i) =>
          `<span class="tag" data-open="${escapeHtml(n.k)}" style="border-color:${colourFor(i)}">
            <span class="legend-swatch line" style="background:${colourFor(i)}"></span>
            ${escapeHtml(n.n)}
            <span class="x" data-remove="${escapeHtml(n.k)}" role="button" aria-label="Remove ${escapeHtml(n.n)}">✕</span>
          </span>`,
      )
      .join('');

    chartEl.innerHTML = '';
    if (!selected.length) {
      chartEl.innerHTML = '<div class="empty">Add a name above to chart it.</div>';
      return;
    }
    const series: LineSeries[] = selected.map((n, i) => {
      const sk = primarySeries(n);
      const pts = (n.series[sk] || []).map((p) => [p[0], metric === 'rank' ? p[1] : p[2]] as [number, number]);
      return { id: n.k, label: n.n, colour: colourFor(i), points: pts };
    });
    const allYears = series.flatMap((s) => s.points.map((p) => p[0]));
    const xDomain: [number, number] = [Math.min(...allYears, 1952), Math.max(meta.latestYear, ...allYears)];
    const svg =
      metric === 'rank'
        ? lineChart(series, {
            width: 1000,
            height: 440,
            xDomain,
            yDomain: [1, 100],
            invertY: true,
            yTickVals: [1, 25, 50, 75, 100],
            yFmt: (v) => `#${Math.round(v)}`,
            endLabels: false,
            onPick: (id) => ctx.openName(id),
          })
        : lineChart(series, {
            width: 1000,
            height: 440,
            xDomain,
            valueNoun: 'babies',
            yFmt: (v) => formatNumber(v),
            endLabels: false,
            onPick: (id) => ctx.openName(id),
          });
    const frame = document.createElement('div');
    frame.className = 'chart-scroll';
    frame.appendChild(svg);
    chartEl.appendChild(frame);
    // series-note
    const note = document.createElement('p');
    note.className = 'stat-note';
    note.style.marginTop = 'var(--space-sm)';
    note.innerHTML = selected
      .map((n, i) => `<span style="color:${colourFor(i)};font-weight:600">${escapeHtml(n.n)}</span> <span style="color:var(--text-tertiary)">(${escapeHtml(seriesLabel(primarySeries(n)))})</span>`)
      .join(' &nbsp;·&nbsp; ');
    chartEl.appendChild(note);
  }

  function add(rec: NameRec): void {
    if (!selected.some((s) => s.k === rec.k)) selected.push(rec);
    addInput.value = '';
    sugg.innerHTML = '';
    paint();
  }

  // add-name search dropdown
  let hits: NameRec[] = [];
  let timer: number | undefined;
  addInput.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hits = searchNames(names, addInput.value);
      sugg.innerHTML = hits
        .map(
          (n) =>
            `<button class="search-item" data-add="${escapeHtml(n.k)}" role="option"><strong>${escapeHtml(n.n)}</strong><span>best #${n.bestRank}</span><em>${formatNumber(n.totalCount)}</em></button>`,
        )
        .join('');
    }, 200);
  });
  addInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && hits.length) {
      e.preventDefault();
      add(hits[0]);
    } else if (e.key === 'Escape') {
      sugg.innerHTML = '';
    }
  });
  sugg.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-add]');
    if (b) add(ctx.data.byKey.get(b.getAttribute('data-add') as string) as NameRec);
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('[data-role="add"]') && !(e.target as Element).closest('[data-role="sugg"]')) sugg.innerHTML = '';
  });

  // tag interactions
  tagsEl.addEventListener('click', (e) => {
    const rm = (e.target as Element).closest('[data-remove]');
    if (rm) {
      const k = rm.getAttribute('data-remove');
      const idx = selected.findIndex((s) => s.k === k);
      if (idx >= 0) selected.splice(idx, 1);
      paint();
      return;
    }
    const open = (e.target as Element).closest('[data-open]');
    if (open) ctx.openName(open.getAttribute('data-open') as string);
  });

  // metric toggle
  root.querySelectorAll('[data-opt]').forEach((b) =>
    b.addEventListener('click', () => {
      metric = b.getAttribute('data-opt') as 'rank' | 'count';
      root.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      paint();
    }),
  );

  paint();
}
